import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

interface ContactProps {
  t: any;
  isRTL: boolean;
}

const ContactSection = styled.section`
  padding: 6rem 2rem;
  background: white;
  position: relative;
`;

const Container = styled.div<{ $isRTL: boolean }>`
  max-width: 1200px;
  margin: 0 auto;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const SectionTitle = styled(motion.h2)<{ $isRTL: boolean }>`
  font-size: 3rem;
  color: ${props => props.theme.colors.secondary};
  margin-bottom: 1rem;
  position: relative;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  background: linear-gradient(135deg, #0f172a 0%, #dc2626 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: ${props => props.theme.colors.primary};
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionSubtitle = styled(motion.h3)<{ $isRTL: boolean }>`
  font-size: 1.2rem;
  color: ${props => props.theme.colors.gray};
  font-weight: 300;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const ContentGrid = styled.div<{ $isRTL: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: start;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
  
  ${props => props.$isRTL && `
    direction: rtl;
  `}
`;

const ContactInfo = styled.div<{ $isRTL: boolean }>`
  padding: 2rem;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  text-align: ${props => props.$isRTL ? 'right' : 'left'};
`;

const ContactItem = styled(motion.div)<{ $isRTL: boolean }>`
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
  padding: 1rem;
  background: white;
  border-radius: 15px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  flex-direction: ${props => props.$isRTL ? 'row-reverse' : 'row'};
  text-align: ${props => props.$isRTL ? 'right' : 'left'};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ContactIcon = styled.div<{ $isRTL: boolean }>`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary} 0%, ${props => props.theme.colors.primaryDark} 100%);
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  ${props => props.$isRTL ? 'margin-left: 1rem;' : 'margin-right: 1rem;'}
  flex-shrink: 0;
  
  img {
    width: 30px;
    height: 30px;
    filter: brightness(0) invert(1);
  }
`;

const ContactDetails = styled.div<{ $isRTL: boolean }>`
  flex: 1;
  text-align: ${props => props.$isRTL ? 'right' : 'left'};
`;

const ContactLabel = styled.h4<{ $isRTL: boolean }>`
  color: ${props => props.theme.colors.secondary};
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const ContactValue = styled.p<{ $isRTL: boolean }>`
  color: ${props => props.theme.colors.gray};
  font-size: 1rem;
  line-height: 1.5;
  margin: 0;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const ContactForm = styled(motion.form)<{ $isRTL: boolean }>`
  background: white;
  padding: 2.5rem;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(220, 38, 38, 0.1);
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const FormLabel = styled.label<{ $isRTL: boolean }>`
  display: block;
  color: ${props => props.theme.colors.secondary};
  font-size: 1rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
  text-align: ${props => props.$isRTL ? 'right' : 'left'};
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const FormInput = styled.input<{ $isRTL: boolean }>`
  width: 100%;
  padding: 1rem 1.5rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 1rem;
  color: ${props => props.theme.colors.secondary};
  background: #f8fafc;
  transition: all 0.3s ease;
  text-align: ${props => props.$isRTL ? 'right' : 'left'};
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    background: white;
    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
  }
  
  &::placeholder {
    color: #94a3b8;
    text-align: ${props => props.$isRTL ? 'right' : 'left'};
  }
`;

const FormTextarea = styled.textarea<{ $isRTL: boolean }>`
  width: 100%;
  padding: 1rem 1.5rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 1rem;
  color: ${props => props.theme.colors.secondary};
  background: #f8fafc;
  transition: all 0.3s ease;
  min-height: 120px;
  resize: vertical;
  font-family: inherit;
  text-align: ${props => props.$isRTL ? 'right' : 'left'};
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    background: white;
    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
  }
  
  &::placeholder {
    color: #94a3b8;
    text-align: ${props => props.$isRTL ? 'right' : 'left'};
  }
`;

const SubmitButton = styled(motion.button)<{ $isRTL: boolean }>`
  background: linear-gradient(135deg, ${props => props.theme.colors.primary} 0%, ${props => props.theme.colors.primaryDark} 100%);
  color: white;
  border: none;
  padding: 1rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 12px;
  cursor: pointer;
  width: 100%;
  transition: all 0.3s ease;
  text-align: center;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(220, 38, 38, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const StatusMessage = styled.div<{ $type: 'success' | 'error' }>`
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  text-align: center;
  border: 1px solid ${props => props.$type === 'success' ? '#c3e6cb' : '#f5c6cb'};
  background: ${props => props.$type === 'success' ? '#d4edda' : '#f8d7da'};
  color: ${props => props.$type === 'success' ? '#155724' : '#721c24'};
`;

const Contact: React.FC<ContactProps> = ({ t, isRTL }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

    const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    
    try {
      // Send email using Firebase function
      const response = await fetch('https://us-central1-techyid-f947c.cloudfunctions.net/sendContactEmail', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to send email');
      }
      
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      
    } catch (error) {
      console.error('Error sending email:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ContactSection id="contact" ref={ref}>
      <Container $isRTL={isRTL}>
        <SectionHeader>
          <SectionTitle
            $isRTL={isRTL}
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            {t.contactTitle}
          </SectionTitle>
          <SectionSubtitle
            $isRTL={isRTL}
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {t.contactSubtitle}
          </SectionSubtitle>
        </SectionHeader>

        <ContentGrid $isRTL={isRTL}>
          <ContactInfo $isRTL={isRTL}>
            <ContactItem
              $isRTL={isRTL}
              initial={{ opacity: 0, x: isRTL ? 50 : -50 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 }}
              as="a"
              href="https://maps.app.goo.gl/utrwQPXAnSzFLL8t5"
              target="_blank"
              rel="noopener noreferrer"
              style={{ 
                textDecoration: 'none', 
                color: 'inherit',
                cursor: 'pointer'
              }}
            >
              <ContactIcon $isRTL={isRTL}>
                <img src="/images/contacts/address.svg" alt={t.altTexts.location} />
              </ContactIcon>
              <ContactDetails $isRTL={isRTL}>
                <ContactLabel $isRTL={isRTL}>{t.altTexts.location}</ContactLabel>
                <ContactValue $isRTL={isRTL}>{t.address}</ContactValue>
              </ContactDetails>
            </ContactItem>

            <ContactItem
              $isRTL={isRTL}
              initial={{ opacity: 0, x: isRTL ? 50 : -50 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
              as="a"
              href="https://api.whatsapp.com/send/?phone=22248610689&text&type=phone_number&app_absent=0"
              target="_blank"
              rel="noopener noreferrer"
              style={{ 
                textDecoration: 'none', 
                color: 'inherit',
                cursor: 'pointer'
              }}
            >
              <ContactIcon $isRTL={isRTL}>
                <img src="/images/footer/whatsapp.svg" alt={t.altTexts.phone} />
              </ContactIcon>
              <ContactDetails $isRTL={isRTL}>
                <ContactLabel $isRTL={isRTL}>{t.altTexts.phone}</ContactLabel>
                <ContactValue $isRTL={isRTL}>{t.phone}</ContactValue>
              </ContactDetails>
            </ContactItem>

            <ContactItem
              $isRTL={isRTL}
              initial={{ opacity: 0, x: isRTL ? 50 : -50 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <ContactIcon $isRTL={isRTL}>
                <img src="/images/contacts/mail.svg" alt={t.altTexts.email} />
              </ContactIcon>
              <ContactDetails $isRTL={isRTL}>
                <ContactLabel $isRTL={isRTL}>{t.altTexts.email}</ContactLabel>
                <ContactValue $isRTL={isRTL}>{t.email}</ContactValue>
              </ContactDetails>
            </ContactItem>
          </ContactInfo>

          <ContactForm
            $isRTL={isRTL}
            initial={{ opacity: 0, x: isRTL ? -50 : 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            onSubmit={handleSubmit}
          >
            {/* Success/Error Messages */}
            {submitStatus === 'success' && (
              <StatusMessage $type="success">
                ✅ {t.messageSent || 'Message sent successfully to contact@techyid.com!'}
              </StatusMessage>
            )}
            
            {submitStatus === 'error' && (
              <StatusMessage $type="error">
                ❌ {t.messageError || 'Failed to send message. Please try again.'}
              </StatusMessage>
            )}

            <FormGroup>
              <FormLabel $isRTL={isRTL} htmlFor="name">{t.contactForm.name}</FormLabel>
              <FormInput
                $isRTL={isRTL}
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder={t.contactForm.name}
              />
            </FormGroup>

            <FormGroup>
              <FormLabel $isRTL={isRTL} htmlFor="email">{t.contactForm.email}</FormLabel>
              <FormInput
                $isRTL={isRTL}
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder={t.contactForm.email}
              />
            </FormGroup>

            <FormGroup>
              <FormLabel $isRTL={isRTL} htmlFor="subject">{t.contactForm.subject}</FormLabel>
              <FormInput
                $isRTL={isRTL}
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                required
                placeholder={t.contactForm.subject}
              />
            </FormGroup>

            <FormGroup>
              <FormLabel $isRTL={isRTL} htmlFor="message">{t.contactForm.message}</FormLabel>
              <FormTextarea
                $isRTL={isRTL}
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                required
                placeholder={t.contactForm.message}
              />
            </FormGroup>

            <SubmitButton
              $isRTL={isRTL}
              type="submit"
              disabled={isSubmitting}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {isSubmitting ? (t.sending || 'Sending...') : t.contactForm.send}
            </SubmitButton>
          </ContactForm>
        </ContentGrid>
      </Container>
    </ContactSection>
  );
};

export default Contact;
